<script setup lang="ts">
import { faPlane, faHotel } from '@fortawesome/pro-solid-svg-icons';
import { ReserveType } from '@utils/itinerary';
import MrProgressBar from '@components/base/MrProgressBar.vue';

const { margin, inFilters } = defineProps<IProps>();
const itineraryStore = useItineraryStore();
const searchFormStore = useSearchFormStore();
const currentModule = useCurrentItineraryModule();
interface IProps {
    margin?: string;
    inFilters?: boolean;
}
const isLoading = computed(
    () =>
        currentModule.value.isRealTimeLoading &&
        !itineraryStore.allPackagesLoaded
);
const info = computed(() => {
    let title;
    let icon;
    switch (searchFormStore.reserveType) {
        case ReserveType.Hotel:
            title = 'در حال جستجوی هتل‌ها...';
            icon = faHotel;
            break;
        case ReserveType.Flight:
        default:
            title = 'در حال جستجوی پروازها...';
            icon = faPlane;
            break;
    }
    return {
        title: inFilters ? 'در حال جستجو...' : title,
        icon,
    };
});

const loadingProgress = ref(0);
const loadingProgressEnded = ref(true);

let timeoutId: number | null = null;
let progressEndTimeoutId: number | null = null;

const progressHandler = () => {
    if (!isLoading.value) {
        return;
    }
    loadingProgress.value += 100 / 60;
    timeoutId = window.setTimeout(() => {
        if (loadingProgress.value <= 100) {
            progressHandler();
        } else {
            endLoadingProgress();
        }
    }, 1000);
};

const clearCurrentTimeout = () => {
    if (timeoutId) {
        clearTimeout(timeoutId);
    }
    if (progressEndTimeoutId) {
        clearTimeout(progressEndTimeoutId);
    }
};

const startLoadingProgress = () => {
    loadingProgress.value = 0;
    loadingProgressEnded.value = false;
    progressHandler();
};

const endLoadingProgress = () => {
    loadingProgress.value = 100;
    progressEndTimeoutId = window.setTimeout(
        () => (loadingProgressEnded.value = true),
        1500
    );
};

watch(
    isLoading,
    val => {
        if (inFilters) {
            return;
        }
        clearCurrentTimeout();
        if (val) {
            startLoadingProgress();
        } else {
            endLoadingProgress();
        }
    },
    { immediate: true }
);

onUnmounted(clearCurrentTimeout);
</script>

<template>
    <div
        v-show="isLoading || !loadingProgressEnded"
        class="real-time-loading-container"
        :style="{ margin }"
    >
        <div
            class="real-time-loading"
            :class="{
                'in-filters': inFilters,
            }"
        >
            <div class="icon-animation-container">
                <div class="circle-animation" />
                <mr-icon
                    class="icon"
                    :icon="info.icon"
                    color="var(--blue-400)"
                    :rotation="180"
                    :size="{
                        mobile: 'x-small',
                        desktop: 'large',
                    }"
                />
            </div>
            <div class="loading-title">
                {{ info.title }}
            </div>
        </div>
        <mr-progress-bar v-if="!inFilters" :progress="loadingProgress" />
    </div>
</template>

<style lang="scss" scoped>
$animation-duration: 1.8s;

.real-time-loading-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 8px;
}
.real-time-loading {
    display: flex;
    align-items: center;
    &.in-filters {
        .loading-title {
            @include tg-s($w7, white);
        }
    }
}

.icon-animation-container {
    position: relative;
    width: 36px;
    height: 36px;
    overflow: hidden;
    @include desktop-only {
        width: 48px;
        height: 48px;
    }
}

.circle-animation {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    animation: loader-animation $animation-duration infinite ease-in-out;
    animation-delay: 100ms;
    border-radius: 100%;
    @include desktop-only {
        width: 48px;
        height: 48px;
    }
}

.icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateY(-50%);
    animation: icon-animation $animation-duration infinite ease-in-out;
    animation-delay: 100ms;
}

@keyframes loader-animation {
    0% {
        background-color: usePalette($p-cool-gray, 200);
        scale: 1;
    }
    20%,
    80% {
        background-color: usePalette($p-blue, 200);
        scale: 0.888888889;
    }

    100% {
        background-color: usePalette($p-cool-gray, 200);
        scale: 1;
    }
}

@keyframes icon-animation {
    0% {
        transform: translate(100%, -50%);
    }
    30%,
    70% {
        transform: translate(-50%, -50%);
    }
    100% {
        transform: translate(-200%, -50%);
    }
}

.loading-title {
    @include tg-m($w5, usePalette($p-cool-gray, $p-600));
    margin-right: 8px;
    white-space: nowrap;
}
</style>
